import { useState } from "react";
import { toast } from "sonner";
import { Loader2, X, Handshake } from "lucide-react";
import { api } from "../../lib/api";

/**
 * ReferBusinessCard — sits directly under the Dedicated Advisor card in the
 * Client Portal and matches its size/styling. Lets a client refer another
 * business: name + phone + business name (required), email optional.
 */
export default function ReferBusinessCard() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div data-testid="refer-card" className="rounded-2xl border border-[#c9a961]/35 bg-white p-5 flex flex-col gap-4 items-start min-w-0 w-full">
        <div className="flex items-center gap-3">
          <div className="h-14 w-14 rounded-full bg-[#eae7de] border border-[#c9a961]/40 flex items-center justify-center shrink-0">
            <Handshake className="h-7 w-7 text-[#1e3d3a]/70" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Spread the word</p>
            <p className="mt-1 font-head text-xl text-[#1e3d3a]">Refer a Business</p>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 mt-0.5">Know a business we can help?</p>
          </div>
        </div>
        <p className="text-sm text-[#1e3d3a]/70 leading-relaxed">
          Send us a business that could use a turnaround. Share their details and our team will reach out.
        </p>
        <button
          type="button"
          data-testid="refer-open"
          onClick={() => setOpen(true)}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
        >
          <Handshake className="h-3.5 w-3.5" />
          Refer a business
        </button>
      </div>
      <ReferModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function ReferModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setName(""); setPhone(""); setBusinessName(""); setEmail(""); setDone(false);
  };

  const close = () => { if (!submitting) { onClose(); setTimeout(reset, 300); } };

  const submit = async () => {
    if (!name.trim() || !phone.trim() || !businessName.trim()) {
      toast.error("Name, phone and business name are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/portal/refer", {
        name: name.trim(),
        phone: phone.trim(),
        business_name: businessName.trim(),
        email: email.trim() || null,
      });
      setDone(true);
      toast.success("Thanks — we'll reach out to them soon.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send that referral. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#1e3d3a]/55 backdrop-blur-sm"
      data-testid="refer-modal"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div role="dialog" aria-modal="true" className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-[#c9a961]/25">
        <button
          type="button"
          data-testid="refer-close"
          onClick={close}
          disabled={submitting}
          className="absolute top-3 right-3 text-[#1e3d3a]/40 hover:text-[#1e3d3a] transition-colors duration-300 p-1"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="px-7 pt-7 pb-4">
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">Refer a business</p>
          <h2 className="font-head text-2xl text-[#1e3d3a] tracking-tight mt-1">
            {done ? "Referral sent" : "Who should we reach out to?"}
          </h2>
        </div>

        {done ? (
          <div data-testid="refer-confirmed" className="px-7 pb-7">
            <div className="rounded-xl border border-[#6b7c3a]/40 bg-[#eae7de]/60 p-5 text-center">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#a3823f]">Thank you</p>
              <p className="mt-2 text-sm text-[#1e3d3a]/70 leading-relaxed">
                We've received your referral and our team will reach out to them shortly.
              </p>
            </div>
            <button
              type="button"
              onClick={close}
              className="mt-5 w-full rounded-full bg-[#1e3d3a] text-white py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="px-7 pb-7 space-y-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-1">Contact name *</label>
              <input
                data-testid="refer-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith"
                className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] placeholder-[#1e3d3a]/30 focus:border-[#c9a961] focus:outline-none transition-colors duration-300"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-1">Phone number *</label>
              <input
                data-testid="refer-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] placeholder-[#1e3d3a]/30 focus:border-[#c9a961] focus:outline-none transition-colors duration-300"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-1">Business name *</label>
              <input
                data-testid="refer-business"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                placeholder="Acme Co."
                className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] placeholder-[#1e3d3a]/30 focus:border-[#c9a961] focus:outline-none transition-colors duration-300"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.22em] text-[#a3823f] mb-1">Email (optional)</label>
              <input
                data-testid="refer-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@acme.com"
                className="w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] placeholder-[#1e3d3a]/30 focus:border-[#c9a961] focus:outline-none transition-colors duration-300"
              />
            </div>
            <button
              type="button"
              data-testid="refer-submit"
              onClick={submit}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1e3d3a] text-white px-5 py-3 text-sm font-medium tracking-wide hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : "Send referral"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
